import {
  EllipsisOutlined,
  EnvironmentOutlined,
  MailOutlined,
  ReloadOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { Button, Dropdown, Flex, Layout, type MenuProps, Space, Tag, Typography } from 'antd';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';
import { useStartupSession } from '@/app/session/StartupSessionContext';
import { AutoSyncStatusTag } from '@/app/shell/AppShellStatus';
import { runAppRefreshHandlers } from '@/app/shell/app-refresh';
import { FixedBottomNavigation } from '@/app/shell/FixedBottomNavigation';
import type { AppView } from '@/app/shell/navigation';
import { getRouteView, inventoryRoutes } from '@/app/shell/routes';
import {
  LocationBootstrapProvider,
  useLocationBootstrap,
} from '@/features/location-bootstrap/LocationBootstrapContext';
import type { FieldAutoSyncStatus } from '@/features/sync/engine/field-auto-sync';
import type { InventoryLocale } from '@/shared/i18n/inventory-i18n';
import { LanguageSwitcher } from '@/shared/i18n/LanguageSwitcher';
import { messages } from './messages';

interface AppShellProps {
  autoSyncStatus: FieldAutoSyncStatus;
  children: ReactNode;
  locale: InventoryLocale;
  onLocaleChange: (locale: InventoryLocale) => void;
}

export function AppShell({ autoSyncStatus, children, locale, onLocaleChange }: AppShellProps) {
  const location = useLocation();
  const activeView: AppView = getRouteView(location.pathname);
  const { activeLocation, changeLocation, snapshot } = useStartupSession();

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Layout.Content style={{ padding: '10px 16px calc(96px + env(safe-area-inset-bottom))' }}>
        <Flex align="flex-start" justify="center">
          <div style={{ maxWidth: 560, width: '100%' }}>
            <LocationBootstrapProvider activeLocation={activeLocation}>
              <AppShellBody
                activeLocation={activeLocation}
                autoSyncStatus={autoSyncStatus}
                email={snapshot.email}
                locale={locale}
                onChangeLocation={changeLocation}
                onLocaleChange={onLocaleChange}
              >
                {children}
              </AppShellBody>
            </LocationBootstrapProvider>
          </div>
        </Flex>
      </Layout.Content>
      <FixedBottomNavigation activeView={activeView} />
    </Layout>
  );
}

const APP_SHELL_MENU_KEYS = {
  changeLocation: 'change-location',
  refresh: 'refresh',
  settings: 'settings',
} as const;

function AppShellBody({
  activeLocation,
  autoSyncStatus,
  children,
  email,
  locale,
  onChangeLocation,
  onLocaleChange,
}: {
  activeLocation: ReturnType<typeof useStartupSession>['activeLocation'];
  autoSyncStatus: FieldAutoSyncStatus;
  children: ReactNode;
  email: string;
  locale: InventoryLocale;
  onChangeLocation: () => void;
  onLocaleChange: (locale: InventoryLocale) => void;
}) {
  const intl = useIntl();
  const history = useHistory();
  const { isLoading, refresh } = useLocationBootstrap();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const isRefreshDisabled = isLoading || isRefreshing || !activeLocation;

  async function handleRefresh() {
    setIsRefreshing(true);

    try {
      await refresh();
      await runAppRefreshHandlers();
    } finally {
      setIsRefreshing(false);
    }
  }

  const menuItems: MenuProps['items'] = [
    {
      disabled: isRefreshDisabled,
      icon: <ReloadOutlined />,
      key: APP_SHELL_MENU_KEYS.refresh,
      label: intl.formatMessage(messages.shellRefresh),
    },
    {
      icon: <SettingOutlined />,
      key: APP_SHELL_MENU_KEYS.settings,
      label: intl.formatMessage(messages.shellSettings),
    },
    {
      type: 'divider',
    },
    {
      icon: <EnvironmentOutlined />,
      key: APP_SHELL_MENU_KEYS.changeLocation,
      label: intl.formatMessage(messages.shellChangeLocation),
    },
  ];

  function handleMenuClick({ key }: { key: string }) {
    switch (key) {
      case APP_SHELL_MENU_KEYS.refresh:
        void handleRefresh();
        break;
      case APP_SHELL_MENU_KEYS.settings:
        history.push(inventoryRoutes.settings);
        break;
      case APP_SHELL_MENU_KEYS.changeLocation:
        onChangeLocation();
        break;
    }
  }

  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
      <Space orientation="vertical" size={4} style={{ width: '100%' }}>
        <Flex align="center" gap="small" justify="space-between">
          <Typography.Title ellipsis level={4} style={{ flex: 1, margin: 0, minWidth: 0 }}>
            {activeLocation?.locationName ?? intl.formatMessage(messages.shellNoLocation)}
          </Typography.Title>
          <Space size={6}>
            <LanguageSwitcher compact onChange={onLocaleChange} value={locale} />
            <Dropdown
              menu={{
                items: menuItems,
                onClick: handleMenuClick,
              }}
              placement="bottomRight"
              trigger={['click']}
            >
              <Button
                aria-label={intl.formatMessage(messages.shellPageMenu)}
                icon={<EllipsisOutlined />}
                loading={isLoading || isRefreshing}
                onClick={event => event.preventDefault()}
              />
            </Dropdown>
          </Space>
        </Flex>
        <Space size={4} wrap>
          <Tag icon={<MailOutlined />} style={{ marginInlineEnd: 0 }}>
            {email}
          </Tag>
          <span style={{ display: 'inline-flex' }}>
            <AutoSyncStatusTag status={autoSyncStatus} />
          </span>
        </Space>
      </Space>

      {children}
    </Space>
  );
}
